'use client';

import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { FeCarousel } from '../../components-internal/FeCarousel';
import { currencyFormat } from '../../helpers/formatters';
import { pushDataLayer } from '../../pushDataLayer';
import { ProductCampaingSelectButton } from './ProductCampaingSelectButton';
import { PromotionCampaign } from '$/(cms)/_modules/cmsService/types';

export const PromotionCampaingsCarousel = (props: {
  campaigns: PromotionCampaign[];
  color: string;
  onCampaingPress: (index: number) => void;
  activePromotionIndex: number;
}) => {
  const CarouselContent = useMemo(() => {
    if (!props.campaigns?.length || props.campaigns.length === 1) {
      return null;
    }
    return props.campaigns.map((campaign, index) => {
      const replaceKeyWord = '{HighRewardValue}';
      const isDinamicallyText =
        campaign?.shortDescription?.includes(replaceKeyWord);
      let correctSD = campaign?.shortDescription ?? '';
      if (isDinamicallyText) {
        const arrayToSearch = campaign?.exclusive_offer?.list_of_gifts ?? [];
        const result = arrayToSearch?.sort(
          (a, b) =>
            Number(b?.gift?.gift_value?.price ?? 0) -
            Number(a?.gift?.gift_value?.price ?? 0),
        );

        const highRewardValue = result?.[0]?.gift?.gift_value?.price ?? 0;
        const highRewardCurrency =
          result?.[0]?.gift?.gift_value?.prefix ?? 'SGD$';
        correctSD = correctSD.replace(
          replaceKeyWord,
          `${currencyFormat(highRewardCurrency)}${currencyFormatterNoDecimal.format(highRewardValue)}`,
        );
      }
      return (
        <div
          key={campaign.name}
          data-component="promotion-campaign-tab"
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className={classNames({
            'pl-3 md:pl-0': index === 0,
            'pr-3 md:pr-0': index === props.campaigns.length - 1,
            'w-[calc(100%-6px)] md:w-[calc(100%-8px)]':
              index !== props.campaigns.length - 1,
            'w-[calc(100%-6px)] md:w-full':
              index === props.campaigns.length - 1,
          })}
        >
          <ProductCampaingSelectButton
            color={props.color}
            title={campaign?.name ?? ''}
            subtitle={correctSD}
            index={index}
            isActive={props.activePromotionIndex === index}
            onPress={() => {
              // A tab switch carries no product: plain `navigate`, no items[] (CMS-1686 #2240).
              pushDataLayer({
                event: 'navigate',
                component_type: 'tab',
                component_title: `Offer Card/Offer Tabs_${campaign.name ?? ''}`,
                action: 'click',
                index: index.toString(),
                ...(campaign.exclusive_offer?.offer_title
                  ? {
                      reward_campaign_name:
                        campaign.exclusive_offer.offer_title,
                    }
                  : {}),
                ...(campaign.exclusive_offer?.title
                  ? { reward_campaign_id: campaign.exclusive_offer.title }
                  : {}),
              });
              props.onCampaingPress(index);
            }}
          />
        </div>
      );
    });
  }, [props]);
  if (!CarouselContent) {
    return null;
  }

  return (
    <FeCarousel
      swiperPropsOverride={{
        centeredSlides: false,
        slidesPerView: 'auto',
        spaceBetween: 0,
        breakpoints: {
          768: {
            spaceBetween: 0,
          },
        },
      }}
      slideClassNames={classNames(
        'flex !h-auto flex-col',
        {
          '!w-[calc(100%/3)]': props.campaigns.length === 3,
          '!w-[calc(100%/2)]': props.campaigns.length === 2,
          '!w-[calc(100%/4)]': props.campaigns.length === 4,
        },
        'min-w-50 md:min-w-58 lg:min-w-70',
      )}
      swiperClassNames="-mx-4 md:mx-0"
      navigationWrapperClassNames="hidden 1280:block"
    >
      {CarouselContent}
    </FeCarousel>
  );
};

export default PromotionCampaingsCarousel;
